import React, { useState, useEffect } from 'react'
import { arrayOf, bool, func, number, shape, string } from 'prop-types'
import { i18n } from '@fs/zion-locale'
import { css } from '@emotion/core'
import { H2, Subheading } from '@fs/zion-ui'
import { scaleLinear } from 'd3-scale'
import { useTranslation } from 'react-i18next'
import { ComposableMap, Geographies, Geography } from 'react-simple-maps'
import useIsComponentMounted from '../../../../../shared/hooks/useIsComponentMounted'
import Tooltip from '../../../../../shared/components/Tooltip/Tooltip'
import NameSelector from '../NameSelector'
import populateChoices from '../name_selector_helper'
import { getTileContentData } from '../../../services/services'

const cssAttribution = css`
  color: #bbb;
  font-size: 11px;
  padding: 10px;
`

const mapStyles = css`
  padding: 10px;
  width: 100%;
`

export default function NameHeatmap({
  region,
  id,
  parsedNames,
  nameArgs,
  nameCounts,
  tileContentDispatch,
  gender = 'male',
}) {
  const [t] = useTranslation()
  const [nameArg, setNameArg] = useState(nameArgs || { name: parsedNames?.firstName?.name, isFirst: true })
  const isComponentMountedRef = useIsComponentMounted()

  const selectedName = nameArg?.name ?? ''
  const selectedIsFirst = nameArg?.isFirst ?? false
  const attribution =
    selectedName === parsedNames?.lastName?.name
      ? t('aam.name-heatmap.surname-attribution')
      : t('aam.name-heatmap.name-attribution')
  const choices = populateChoices({ selected: selectedName, names: parsedNames, isFirst: selectedIsFirst })

  const highestCountLocation = nameCounts?.counts?.length
    ? nameCounts?.counts.reduce((prev, current) => (prev?.count > current?.count ? prev : current))
    : 0
  const total = nameCounts?.counts
    ? nameCounts?.counts.reduce((accum, current) => accum + Number.parseInt(current?.count, 10), 0)
    : 0

  const upperRangeColor = gender === 'male' ? '#01b6d1' : '#fc4e6d' // Billboard blue20 and billboard red20

  const colorScale = scaleLinear().domain([0, highestCountLocation?.count]).range(['#000000', upperRangeColor])

  const [mapTopo, setMapTopo] = useState(null)
  let topoFile = ''
  let composableMapOptions = {}

  if (region === 'us') {
    topoFile = 'usTopo.json'
    composableMapOptions = {
      projection: 'geoAlbersUsa',
    }
  } else if (region === 'fr') {
    topoFile = 'frTopo.json'
    composableMapOptions = {
      projection: 'geoMercator',
      projectionConfig: {
        scale: 1400,
        center: [2, 44.9],
      },
    }
  }

  const [tooltipContent, setTooltipContent] = useState('')
  const [tooltipX, setTooltipX] = useState(0)
  const [tooltipY, setTooltipY] = useState(0)
  const [showTooltip, setShowTooltip] = useState(false)

  useEffect(() => {
    if (nameArgs) {
      setNameArg(nameArgs)
    }
  }, [nameArgs])

  useEffect(() => {
    if (isComponentMountedRef.current) {
      import(`./data/${topoFile}`).then(({ default: topoData }) => {
        if (isComponentMountedRef.current) setMapTopo(topoData)
      })
    }
  }, [topoFile])

  return (
    <div css={mapStyles}>
      {mapTopo && (
        <>
          <NameSelector
            justify="center"
            choices={choices}
            selectionChange={(selectedChip) => {
              setNameArg({ name: selectedChip?.value, isFirst: selectedChip?.isFirst })
              getTileContentData({
                supportedTileId: 'nameheatmap',
                countryCode: region,
                tileId: id,
                nameArgs: { name: selectedChip?.value, isFirst: selectedChip?.isFirst },
              }).then((res) => {
                tileContentDispatch({ type: 'complete', res })
              })
            }}
          />
          <H2 centered>
            {new Intl.NumberFormat('en-us').format(total)}
            <Subheading>{t('aam.name-heatmap.people-sharing-name', 'People who share this name')}</Subheading>
          </H2>

          <ComposableMap data-tip="" {...composableMapOptions}>
            <Geographies geography={mapTopo}>
              {({ geographies }) => (
                <>
                  {nameCounts?.counts?.length > 0 &&
                    geographies.map((geo) => {
                      const d = nameCounts.counts.find((item) => {
                        return item.place === geo.properties.name || item.place === geo.id
                      })
                      return (
                        <Geography
                          key={geo.rsmKey}
                          stroke="#FFF"
                          geography={geo}
                          onMouseMove={(event) => {
                            setTooltipX(event.clientX)
                            setTooltipY(event.clientY)
                            setShowTooltip(true)
                            const { name } = geo.properties
                            setTooltipContent(`${name} ${d?.count ? ` - ${d.count}` : ''}`)
                          }}
                          onMouseLeave={() => {
                            setShowTooltip(false)
                            setTooltipContent('')
                          }}
                          style={{
                            default: {
                              outline: 'none',
                            },
                            hover: {
                              outline: 'none',
                            },
                            pressed: {
                              fill: '#E42',
                              outline: 'none',
                            },
                          }}
                          fill={d ? colorScale(d.count) : '#000000'}
                        />
                      )
                    })}
                </>
              )}
            </Geographies>
          </ComposableMap>
          <Tooltip
            tooltipContent={tooltipContent}
            showTooltip={showTooltip}
            tooltipX={tooltipX}
            tooltipY={tooltipY}
            setTooltipX={setTooltipX}
            setTooltipY={setTooltipY}
          />
          {i18n.language === 'fr' && <div css={cssAttribution}>{attribution}</div>}
        </>
      )}
    </div>
  )
}

NameHeatmap.propTypes = {
  region: string,
  id: number,
  parsedNames: shape({
    firstName: shape({}),
    lastName: shape({}),
    middleNames: arrayOf(
      shape({
        name: string,
      })
    ),
  }),
  tileContentData: shape({
    complete: bool,
    error: bool,
    load: bool,
    pending: bool,
    res: arrayOf(
      shape({
        category: string,
        categoryId: number,
        icon: string,
        id: number,
        languageOnly: string,
        localeOnly: string,
        price: number,
        subcategory: string,
        textHeadline: string,
        textTitle: string,
        tileType: string,
        year: number,
      })
    ),
  }),
  tileContentDispatch: func,
}
